import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import Footer from '@/components/Footer';
import PageSeo from '@/components/PageSeo';
import {
  SPEED_GUIDE, SPEED_GUIDE_PATH, SPEED_GUIDE_FAQS, ROUTES, RESULTS, KEY_FINDINGS, SECTIONS, METHODOLOGY,
} from '@/content/guides/flightSearchSpeed';

const MAX = Math.max(...RESULTS.map((r) => r.avg));

/** The October 2026 flight search speed benchmark (src/content/guides/flightSearchSpeed.js). */
export default function FastestFlightSearchEngines() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <PageSeo path={SPEED_GUIDE_PATH} />

      <div className="min-h-screen bg-black text-white">
        <div className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-xl border-b border-white/[0.08]">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/guides" className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors text-sm">
              <ArrowLeft className="w-4 h-4" />
              Guides
            </Link>
            <button
              onClick={() => navigate('/search')}
              className="bg-white text-black px-4 py-1.5 rounded-full text-sm font-medium hover:bg-white/90 transition-colors"
            >
              Search Flights
            </button>
          </div>
        </div>

        <article className="pt-28 pb-20 px-6">
          <div className="max-w-3xl mx-auto">
            <nav aria-label="Breadcrumb" className="text-sm text-white/45 mb-6">
              <Link to="/" className="hover:text-white/70">Home</Link>
              <span className="mx-2">›</span>
              <Link to="/guides" className="hover:text-white/70">Guides</Link>
            </nav>

            <p className="text-xs uppercase tracking-[0.16em] text-white/55 mb-4">{SPEED_GUIDE.kicker}</p>
            <h1 className="text-3xl md:text-5xl font-semibold tracking-tight leading-tight mb-5">{SPEED_GUIDE.title}</h1>
            <p className="text-lg md:text-xl text-white/75 leading-relaxed mb-4">{SPEED_GUIDE.dek}</p>
            <p className="text-sm text-white/45 mb-12">By {SPEED_GUIDE.author} · Updated {SPEED_GUIDE.updated}</p>

            <section className="mb-14">
              <h2 className="text-2xl font-semibold mb-5">Key findings</h2>
              <ul className="space-y-3">
                {KEY_FINDINGS.map((f) => (
                  <li key={f} className="flex gap-3 text-white/80 leading-relaxed">
                    <span className="mt-2.5 h-1.5 w-1.5 rounded-full bg-white/50 shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section className="mb-14">
              <h2 className="text-2xl font-semibold mb-2">Average seconds to first flight results</h2>
              <p className="text-white/55 text-sm mb-6">Lower is faster. Mean of six route averages.</p>
              <ol className="border-t border-white/15">
                {RESULTS.map((r, i) => (
                  <li key={r.site} className="grid grid-cols-[2rem_9.5rem_1fr_3.5rem] sm:grid-cols-[2.5rem_12rem_1fr_4rem] items-center gap-2 py-2.5 border-b border-white/[0.08]">
                    <span className="text-sm text-white/45 tabular-nums">{i + 1}</span>
                    <span className="text-[15px] sm:text-base text-white/90 truncate">{r.site}</span>
                    <span className="h-3 bg-white/[0.06]">
                      <span className="block h-full bg-white/70" style={{ width: `${(r.avg / MAX) * 100}%` }} />
                    </span>
                    <span className="text-right text-base font-medium tabular-nums">{r.avg.toFixed(2)}</span>
                  </li>
                ))}
              </ol>
            </section>

            <section className="mb-14">
              <h2 className="text-2xl font-semibold mb-2">Results by route</h2>
              <p className="text-white/55 text-sm mb-6">Average seconds to first results on each round-trip route.</p>
              <div className="overflow-x-auto -mx-6 px-6">
                <table className="w-full min-w-[640px] text-sm border-collapse">
                  <thead>
                    <tr className="text-white/55 text-left">
                      <th className="py-2 pr-3 font-medium">Site</th>
                      {ROUTES.map((r) => (
                        <th key={r.code} className="py-2 px-2 font-medium text-right" title={r.label}>{r.code}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {RESULTS.map((r) => (
                      <tr key={r.site} className="border-t border-white/[0.08]">
                        <td className="py-2 pr-3 text-white/90 whitespace-nowrap">{r.site}</td>
                        {r.routes.map((s, j) => (
                          <td key={ROUTES[j].code} className="py-2 px-2 text-right tabular-nums text-white/80">{s.toFixed(2)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <ul className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm text-white/55">
                {ROUTES.map((r) => <li key={r.code}><span className="text-white/75">{r.code}</span> · {r.label} ({r.kind.toLowerCase()})</li>)}
              </ul>
            </section>

            {SECTIONS.map(([heading, paragraphs]) => (
              <section key={heading} className="mb-12">
                <h2 className="text-2xl font-semibold mb-4">{heading}</h2>
                {paragraphs.map((p) => <p key={p} className="text-white/80 leading-relaxed mb-4">{p}</p>)}
              </section>
            ))}

            <section className="mb-14">
              <h2 className="text-2xl font-semibold mb-4">How we tested</h2>
              {METHODOLOGY.map((p) => <p key={p} className="text-[15px] text-white/60 leading-relaxed mb-3">{p}</p>)}
            </section>

            <section className="mb-14">
              <h2 className="text-2xl font-semibold mb-6">Frequently asked questions</h2>
              <div className="divide-y divide-white/[0.08] border-y border-white/[0.08]">
                {SPEED_GUIDE_FAQS.map((f) => (
                  <div key={f.q} className="py-5">
                    <h3 className="text-lg font-semibold mb-2">{f.q}</h3>
                    <p className="text-white/75 leading-relaxed">{f.a}</p>
                  </div>
                ))}
              </div>
            </section>

            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <p className="text-xl font-semibold mb-1">Search flights on Defera</p>
                <p className="text-white/60">Book now and pay over time, with no credit check and no interest.</p>
              </div>
              <button
                onClick={() => navigate('/search')}
                className="inline-flex items-center justify-center gap-2 bg-white text-black px-5 py-2.5 rounded-full text-sm font-medium hover:bg-white/90 transition-colors"
              >
                Search flights <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </article>

        <Footer />
      </div>
    </>
  );
}
